/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Card,
  Paper,
  Stack,
  Typography,
  IconButton,
  Collapse,
  Snackbar,
  Alert,
  Slide,
  SlideProps,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import * as signalR from "@microsoft/signalr";
import {
  Refresh,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  Archive,
  ExpandMore,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { downloadStart } from "../../services/downloadService";
import { JOB_STATUS, JobStatus } from "../../data/constants";
import { ZipProgressDto } from "../../types/Download";
import { DOWNLOADVALIDATIONS } from "../../constants/messages";
import { useSignalRConnections } from "../../context/SignalRProvider";
import { logAuditTrace } from "../../services/auditNavigationTraceService";
import { FolderFileType, FileActions } from "../../data/Enums";
import { getCurrentESTTime } from "../../utils/dateHelper";
import { useDispatch } from "react-redux";

export interface ZipJob {
  jobId: string;
  folderId: number;
  name: string;
  progress: number;
  status: JobStatus;
  url?: string;
  downloaded?: boolean;
}

export interface DownloadManagerProps {
  userId: number;
  apiBase: string;
}

export interface DownloadManagerHandles {
  startDownload: (folderId: number, folderName: string) => void;
}

const gradientAnimation = keyframes`
  0% { stroke: #f57f43; }
  25% { stroke: #ffa726; }
  50% { stroke: #fb8c00; }
  75% { stroke: #ffb74d; }
  100% { stroke: #f57f43; }
`;

const DownloadManager = React.forwardRef<DownloadManagerHandles, DownloadManagerProps>(
  ({ userId, apiBase }, ref) => {
    DownloadManager.displayName = "DownloadManager";
    const [jobs, setJobs] = useState<ZipJob[]>([]);
    const [collapsed, setCollapsed] = useState(true);
    const [visible, setVisible] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [countScale, setCountScale] = useState(1);
    const [firstJobAdded, setFirstJobAdded] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const hubRef = useRef<signalR.HubConnection | null>(null);
    const { zipProgressConnection } = useSignalRConnections();
    const dispatch = useDispatch();
    const SlideUp = (props: SlideProps) => <Slide {...props} direction="up" />;

    // Load saved jobs
    useEffect(() => {
      const saved = localStorage.getItem("downloadJobs");
      if (saved) {
        const loadedJobs = JSON.parse(saved);
        if (loadedJobs.length > 0) {
          setJobs(loadedJobs);
          setVisible(true);
          setCollapsed(true);
        }
      }
    }, []);

    // Animate count bounce
    useEffect(() => {
      if (jobs.length > 0) {
        setCountScale(0.5);
        const t1 = setTimeout(() => setCountScale(1.2), 150);
        const t2 = setTimeout(() => setCountScale(1), 300);
        return () => {
          clearTimeout(t1);
          clearTimeout(t2);
        };
      }
    }, [jobs.length]);

    // Auto-hide when no jobs
    useEffect(() => {
      if (jobs.length === 0) setVisible(false);
    }, [jobs]);

    // Throttle localStorage writes
    useEffect(() => {
      const handle = setTimeout(() => {
        localStorage.setItem("downloadJobs", JSON.stringify(jobs));
      }, 500);
      return () => clearTimeout(handle);
    }, [jobs]);

    // Setup SignalR
    useEffect(() => {
      const createHubConnection = async () => {
        try {
          const hub = zipProgressConnection;

          hub.on("ProgressUpdated", (payload: ZipProgressDto) => {
            setJobs((prev) => {
              const index = prev.findIndex((job) => job.jobId.startsWith(payload.jobId));
              if (index === -1) return prev;

              const job = prev[index];
              const updatedJob: ZipJob = {
                ...job,
                progress: payload.progress,
                status: payload.completed
                  ? JOB_STATUS.COMPLETED
                  : payload.error
                    ? JOB_STATUS.FAILED
                    : JOB_STATUS.ZIPPING,
                url: payload.sasUrl ?? job.url,
                downloaded: job.downloaded,
              };

              if (
                updatedJob.status === JOB_STATUS.COMPLETED &&
                updatedJob.url &&
                !updatedJob.downloaded
              ) {
                updatedJob.downloaded = true;

                setTimeout(() => {
                  const a = document.createElement("a");
                  if (updatedJob.url) {
                    a.href = updatedJob.url;
                  }
                  a.download = `${updatedJob.name}.zip`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }, 50);

                if ("Notification" in window && Notification.permission === "granted") {
                  new Notification("Download Complete", { body: updatedJob.name });
                }

                setTimeout(() => {
                  setJobs((jobs) => jobs.filter((j) => j.jobId !== updatedJob.jobId));
                }, 3000);
              }

              const newJobs = [...prev];
              newJobs[index] = updatedJob;
              return newJobs;
            });
          });

          hubRef.current = hub;
        } catch (err) {
          setTimeout(createHubConnection, 3000);
        }
      };

      createHubConnection();

      return () => {
        hubRef.current?.stop().catch(console.error);
      };
    }, [apiBase]);

    // Ensure hub is connected
    const ensureConnected = async () => {
      if (!hubRef.current) throw new Error("Hub not initialized");

      if (hubRef.current.state === signalR.HubConnectionState.Connected) return;

      if (hubRef.current.state === signalR.HubConnectionState.Disconnected) {
        await hubRef.current.start();
      }
    };

    // Start download
    const startDownload = useCallback(
      async (folderId: number, folderName: string) => {
        try {
          await ensureConnected();

          const res = await downloadStart(userId, folderId);
          const { jobId } = res.data;
          logAuditTrace(
            {
              userId: userId,
              objectType: FolderFileType[FolderFileType.Folder],
              objectId: folderId,
              objectName: folderName,
              action: FileActions.Download.toString(),
              navigationDateTime: getCurrentESTTime(),
            },
            dispatch
          );
          setConfirmClose(false);
          setVisible(true);

          setCollapsed((prev) => {
            if (!firstJobAdded) {
              setFirstJobAdded(true);
              return false;
            }
            return prev;
          });

          setJobs((prev) => [
            ...prev,
            {
              jobId: `${jobId}-${Date.now()}`,
              folderId,
              name: folderName,
              progress: 0,
              status: JOB_STATUS.PENDING,
            },
          ]);

          if (hubRef.current) {
            await hubRef.current.invoke("JoinJobGroup", jobId);
          }
        } catch (err) {
          setError(`Download start failed: ${err instanceof Error ? err.message : String(err)}`);
        }
      },
      [userId, firstJobAdded]
    );

    React.useImperativeHandle(ref, () => ({ startDownload }));

    const handleCloseManager = () => {
      if (jobs.some((j) => j.status !== JOB_STATUS.COMPLETED && j.status !== JOB_STATUS.FAILED)) {
        setConfirmClose(true);
      } else {
        doCancelDownloads();
      }
    };

    const doCancelDownloads = useCallback(() => {
      setJobs([]);
      setCollapsed(true);
      setVisible(false);
      setFirstJobAdded(false);
      setConfirmClose(false);
    }, []);

    const cancelJob = useCallback((jobId: string) => {
      setJobs((prev) => prev.filter((job) => job.jobId !== jobId));
    }, []);

    const retryJob = useCallback(
      (job: ZipJob) => {
        if (job.status === JOB_STATUS.FAILED) startDownload(job.folderId, job.name);
      },
      [startDownload]
    );

    if (!visible) return null;

    return (
      <>
        <Slide in={visible} direction="up">
          <Paper
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              width: collapsed ? 140 : 400,
              height: collapsed ? 40 : "auto",
              p: collapsed ? 0.5 : 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(255,255,255,0.95)",
              zIndex: 1500,
              overflow: "hidden",
            }}
          >
            {collapsed ? (
              <Box display="flex" alignItems="center" justifyContent="center" width="100%" gap={1}>
                <Typography fontWeight={600} variant="caption">
                  Downloads (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      transform: `scale(${countScale})`,
                      transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1)",
                    }}
                  >
                    {jobs.length}
                  </Box>
                  )
                </Typography>
                <IconButton size="small" onClick={() => setCollapsed(false)}>
                  <ExpandMore
                    sx={{
                      fontSize: 20,
                      color: "#f57f43",
                      transform: "rotate(180deg)",
                      transition: "transform 0.3s",
                    }}
                  />
                </IconButton>
              </Box>
            ) : (
              <Stack spacing={1} sx={{ width: "100%" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Downloads ({jobs.length})
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <IconButton onClick={() => setCollapsed(true)}>
                      <ExpandMore sx={{ color: "#f57f43", transform: "rotate(0deg)" }} />
                    </IconButton>
                    <IconButton size="small" onClick={handleCloseManager}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Stack spacing={1}>
                  {jobs.map((job) => (
                    <CircularJobItem
                      key={job.jobId}
                      job={job}
                      retryJob={retryJob}
                      cancelJob={cancelJob}
                    />
                  ))}
                </Stack>
              </Stack>
            )}

            <Snackbar
              open={!!error}
              autoHideDuration={5000}
              onClose={() => setError(null)}
              TransitionComponent={SlideUp}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert severity="info" variant="filled" onClose={() => setError(null)}>
                {error}
              </Alert>
            </Snackbar>
          </Paper>
        </Slide>

        {/* Confirmation Dialog */}
        <Dialog open={confirmClose} onClose={() => setConfirmClose(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Cancel download?</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>{DOWNLOADVALIDATIONS.CANCEL_DOWNLOAD_CONFIRMATION}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmClose(false)} color="primary" variant="outlined">
              Continue Download
            </Button>
            <Button onClick={doCancelDownloads} color="error" variant="contained">
              Cancel Download
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);
DownloadManager.displayName = "DownloadManager";
export default DownloadManager;

// --------------------- Job Item Component ---------------------
interface CircularJobItemProps {
  job: ZipJob;
  retryJob: (job: ZipJob) => void;
  cancelJob: (jobId: string) => void;
}

const CircularJobItem: React.FC<CircularJobItemProps> = React.memo(
  ({ job, retryJob, cancelJob }) => {
    const [visible, setVisible] = useState(true);

    useEffect(() => {
      if (job.status === "completed" && job.downloaded) {
        const timer = setTimeout(() => setVisible(false), 3000);
        return () => clearTimeout(timer);
      }
    }, [job.status, job.downloaded]);

    const getInnerIcon = () => {
      if (job.status === "completed")
        return <CheckCircle color="success" fontSize="inherit" sx={{ fontSize: 22 }} />;
      if (job.status === "failed")
        return <ErrorIcon color="error" fontSize="inherit" sx={{ fontSize: 22 }} />;
      return (
        <Typography sx={{ fontSize: "0.55rem" }} color="textSecondary">
          {`${Math.round(job.progress)}%`}
        </Typography>
      );
    };

    const getStatusText = () => {
      switch (job.status) {
        case JOB_STATUS.PENDING:
          return "Pending";
        case JOB_STATUS.ZIPPING:
          return "Zipping";
        case JOB_STATUS.COMPLETED:
          return "Completed";
        case JOB_STATUS.FAILED:
          return "Failed";
        default:
          return "";
      }
    };

    return (
      <Collapse in={visible} onExited={() => cancelJob(job.jobId)}>
        <Card
          variant="outlined"
          sx={{
            p: 1,
            transition: "all 0.2s",
            cursor: "pointer",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 4,
              backgroundColor: "rgba(255,255,255,0.05)",
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Archive sx={{ color: "#f57f43" }} />
            <Box flex={1}>
              <Typography noWrap fontWeight={500} variant="body2">
                {job.name.length > 18 ? job.name.slice(0, 18) + "..." : job.name}
              </Typography>
            </Box>
            <Typography sx={{ minWidth: 60 }} variant="body2" color="textSecondary">
              {getStatusText()}
            </Typography>
            <Box position="relative" display="inline-flex" ml={1}>
              <Box
                sx={{
                  position: "absolute",
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  backgroundColor: "rgba(0,0,0,0.05)",
                  filter: "blur(4px)",
                  top: -2,
                  left: -2,
                }}
              />
              <CircularProgress
                variant="determinate"
                value={100}
                size={30}
                thickness={6}
                sx={{ color: "#e0e0e0" }}
              />
              <CircularProgress
                variant="determinate"
                value={job.progress}
                size={30}
                thickness={6}
                sx={{
                  position: "absolute",
                  left: 0,
                  color: "transparent",
                  "& .MuiCircularProgress-circle": {
                    stroke: "url(#gradient)",
                    strokeLinecap: "round",
                    animation: `${gradientAnimation} 3s linear infinite`,
                  },
                }}
              />
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                alignItems="center"
                justifyContent="center"
                display="flex"
              >
                {getInnerIcon()}
              </Box>
              <svg width="0" height="0">
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop stopColor="#f57f43" offset="0%" />
                  <stop stopColor="#ffa726" offset="25%" />
                  <stop stopColor="#fb8c00" offset="50%" />
                  <stop stopColor="#ffb74d" offset="75%" />
                  <stop stopColor="#f57f43" offset="100%" />
                </linearGradient>
              </svg>
            </Box>
            <Box ml={1} display="flex" gap={0.5} alignItems="center">
              {job.status === "failed" && (
                <IconButton size="small" onClick={() => retryJob(job)}>
                  <Refresh fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={() => cancelJob(job.jobId)}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Stack>
        </Card>
      </Collapse>
    );
  }
);
CircularJobItem.displayName = "CircularJobItem";
